/**
 * useModalScrollLock — modal ochiq bo'lganda orqa sahifa (<main>) scroll'ini qulflaydi.
 *
 * Muammo: ilovada ~16 ta custom modal (`fixed inset-0 bg-black/*`) + Radix dialoglar
 * bor. Modal ochiq bo'lsa, orqa <main> scroll bo'lib ketardi (foydalanuvchi shikoyati).
 *
 * Yechim: bitta global MutationObserver body'ni kuzatadi. Har qanday modal overlay
 * DOM'da paydo bo'lsa — scroll konteyneri (<main>) `overflow:hidden` bo'ladi
 * (scrollbar yo'qolishidan kelib chiqadigan siljish `paddingRight` bilan
 * kompensatsiya qilinadi). Barcha modal yopilsa — asl holatga tiklanadi.
 * Ichma-ich (nested) modallar uchun ham to'g'ri ishlaydi (faqat overlay borligini
 * tekshiradi — sanoq emas). Har modalga alohida kod kerak emas.
 *
 * Selektor:
 *   - `[data-slot$="-overlay"]` — Radix dialog/alert-dialog/sheet overlaylari (body'ga portal)
 *   - `.fixed.inset-0[class*="bg-black/"]` — barcha custom modal overlaylari
 */
import { useEffect, type RefObject } from 'react';

import { MODAL_OVERLAY_SELECTOR as MODAL_SELECTOR } from '@/shared/lib/modal-overlay';

export function useModalScrollLock(scrollRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return undefined;

    let locked = false;
    let prevOverflow = '';
    let prevPad = '';

    const update = (): void => {
      const hasModal = document.querySelector(MODAL_SELECTOR) !== null;
      if (hasModal && !locked) {
        const scrollbarWidth = el.offsetWidth - el.clientWidth;
        prevOverflow = el.style.overflow;
        prevPad = el.style.paddingRight;
        el.style.overflow = 'hidden';
        if (scrollbarWidth > 0) el.style.paddingRight = `${String(scrollbarWidth)}px`;
        locked = true;
      } else if (!hasModal && locked) {
        el.style.overflow = prevOverflow;
        el.style.paddingRight = prevPad;
        locked = false;
      }
    };

    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    update();

    return () => {
      observer.disconnect();
      if (locked) {
        el.style.overflow = prevOverflow;
        el.style.paddingRight = prevPad;
      }
    };
  }, [scrollRef]);
}
